import { useState, useMemo, useCallback, useEffect, type ReactElement } from 'react'
import { Link } from 'react-router-dom'
import type {
  JiraConnectionInfo,
  JiraInstanceInfo,
  JiraBoard,
  JiraSprint,
  JiraSprintIssue,
  BoardsReason,
} from '../api'
import { getJiraBoards, getJiraSprints, getJiraSprintIssues, addSavedProjectKey, removeSavedProjectKey } from '../api'

// ─── Props ──────────────────────────────────────────────────────
interface SprintImportModalProps {
  onClose: () => void
  onImport: (keys: string[]) => void
  appMode: 'test-case-updater' | 'risk-prioritization'
  jiraStatus: JiraConnectionInfo | null
}

// ─── Internal Types ─────────────────────────────────────────────

interface MappedIssue {
  key: string
  summary: string
  status: { name: string; category: 'todo' | 'in_progress' | 'done' | 'cancelled' }
  type: { name: string; icon: 'story' | 'bug' | 'task' | 'subtask' | 'epic' }
  priority: { name: string; level: 'highest' | 'high' | 'medium' | 'low' | 'lowest' }
}

type SprintStateFilter = 'active' | 'future' | 'closed'

// ─── Mapping Functions ──────────────────────────────────────────

function mapStatusCategory(color: string): 'todo' | 'in_progress' | 'done' | 'cancelled' {
  if (color === 'green') return 'done'
  if (color === 'blue-gray' || color === 'medium-gray') return 'todo'
  return 'in_progress'
}

function mapIssueTypeIcon(typeName: string): 'story' | 'bug' | 'task' | 'subtask' | 'epic' {
  const lower = typeName.toLowerCase()
  if (lower.includes('bug')) return 'bug'
  if (lower.includes('story') || lower.includes('user story')) return 'story'
  if (lower.includes('epic')) return 'epic'
  if (lower.includes('sub')) return 'subtask'
  return 'task'
}

function mapPriorityLevel(name: string): 'highest' | 'high' | 'medium' | 'low' | 'lowest' {
  const lower = name.toLowerCase()
  if (lower === 'highest' || lower === 'critical' || lower === 'blocker') return 'highest'
  if (lower === 'high' || lower === 'major') return 'high'
  if (lower === 'low' || lower === 'minor') return 'low'
  if (lower === 'lowest' || lower === 'trivial') return 'lowest'
  return 'medium'
}

function mapIssue(issue: JiraSprintIssue): MappedIssue {
  return {
    key: issue.key,
    summary: issue.summary,
    status: { name: issue.status.name, category: mapStatusCategory(issue.status.color) },
    type: { name: issue.type.name, icon: mapIssueTypeIcon(issue.type.name) },
    priority: { name: issue.priority.name, level: mapPriorityLevel(issue.priority.name) },
  }
}

// ─── Completed Issue Detection ──────────────────────────────────

const EXCLUDED_STATUSES = ['done', "won't do", "won't fix", 'cancelled', 'canceled', 'closed', 'rejected', 'resolved', 'complete']

function isCompletedIssue(issue: MappedIssue): boolean {
  return EXCLUDED_STATUSES.includes(issue.status.name.toLowerCase()) ||
    issue.status.category === 'done' ||
    issue.status.category === 'cancelled'
}

// ─── Inline Shared Components ───────────────────────────────────

function SearchInput({
  value,
  onChange,
  placeholder,
  className = '',
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  className?: string
}) {
  return (
    <div className={`relative ${className}`}>
      <svg
        className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-linear-text-muted"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M11.5 11.5L14 14M7.5 12.5a5 5 0 100-10 5 5 0 000 10z"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="input input-sm !pl-8 !text-xs !py-1.5"
      />
    </div>
  )
}

function SprintBadge({ state }: { state: string }) {
  const map: Record<string, string> = {
    active: 'badge-success',
    future: 'badge-info',
    closed: 'badge-neutral',
  }
  const label = state.charAt(0).toUpperCase() + state.slice(1)
  return <span className={`badge ${map[state] ?? 'badge-neutral'}`}>{label}</span>
}

function InstanceAvatar({ name }: { name: string }) {
  const letter = name.charAt(0).toUpperCase()
  return (
    <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-linear-accent/20 text-linear-accent text-[10px] font-semibold shrink-0">
      {letter}
    </span>
  )
}

// ─── SVG Icons ──────────────────────────────────────────────────

function ChevronDown({ className = 'w-3.5 h-3.5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M4 6l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M4 4l8 8M12 4l-8 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

function CheckIcon({ checked }: { checked: boolean }) {
  if (!checked) return null
  return (
    <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M2.5 6l2.5 2.5 4.5-5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// ─── Issue Type Icons ───────────────────────────────────────────

type IssueIconType = 'story' | 'bug' | 'task' | 'subtask' | 'epic'

const issueTypeIcons: Record<IssueIconType, { color: string; path: ReactElement }> = {
  story: {
    color: '#4CB782',
    path: (
      <path
        d="M4 2a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h2.5a.5.5 0 0 0 .5-.5V11a1 1 0 0 1 1-1h.5a1 1 0 0 1 1 1v2.5a.5.5 0 0 0 .5.5H12a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1H4z"
        fill="currentColor"
      />
    ),
  },
  bug: {
    color: '#EB5757',
    path: (
      <g fill="currentColor">
        <circle cx="8" cy="9" r="4" />
        <path d="M6.5 3.5a1.5 1.5 0 0 1 3 0v1h-3v-1z" />
        <path d="M2 7h2.5M11.5 7H14M2 11h2.5M11.5 11H14M3.5 4L5 5.5M11 5.5L12.5 4" strokeWidth="1.2" stroke="currentColor" fill="none" strokeLinecap="round" />
      </g>
    ),
  },
  task: {
    color: '#575BC7',
    path: (
      <g fill="currentColor">
        <rect x="2" y="2" width="12" height="12" rx="2" opacity="0.2" />
        <path
          d="M5.5 8l2 2 3.5-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    ),
  },
  subtask: {
    color: '#00B2BF',
    path: (
      <g fill="currentColor">
        <rect x="3" y="3" width="10" height="10" rx="2" opacity="0.2" />
        <path
          d="M6 8l1.5 1.5L10 7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    ),
  },
  epic: {
    color: '#A855F7',
    path: (
      <path
        d="M8.5 1.5L4 9h3.5v5.5L12 7H8.5V1.5z"
        fill="currentColor"
      />
    ),
  },
}

function IssueTypeIcon({ type }: { type: IssueIconType }) {
  const icon = issueTypeIcons[type]
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className="w-4 h-4 shrink-0"
      style={{ color: icon.color }}
      aria-label={type}
    >
      {icon.path}
    </svg>
  )
}

// ─── Priority Icons ─────────────────────────────────────────────

type PriorityLevel = 'highest' | 'high' | 'medium' | 'low' | 'lowest'

const priorityConfig: Record<PriorityLevel, { color: string; label: string }> = {
  highest: { color: '#EB5757', label: 'Highest' },
  high:    { color: '#F2994A', label: 'High' },
  medium:  { color: '#F2C94C', label: 'Medium' },
  low:     { color: '#575BC7', label: 'Low' },
  lowest:  { color: '#858699', label: 'Lowest' },
}

function PriorityArrowUp({ color }: { color: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="w-4 h-4 shrink-0" aria-hidden="true">
      <path
        d="M8 3v10M8 3l3.5 3.5M8 3L4.5 6.5"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function PriorityArrowDown({ color }: { color: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="w-4 h-4 shrink-0" aria-hidden="true">
      <path
        d="M8 13V3M8 13l3.5-3.5M8 13L4.5 9.5"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function PriorityDash({ color }: { color: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="w-4 h-4 shrink-0" aria-hidden="true">
      <path
        d="M4 8h8"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function PriorityIcon({ level }: { level: PriorityLevel }) {
  const { color, label } = priorityConfig[level]

  return (
    <span title={label}>
      {level === 'highest' && <PriorityArrowUp color={color} />}
      {level === 'high' && <PriorityArrowUp color={color} />}
      {level === 'medium' && <PriorityDash color={color} />}
      {level === 'low' && <PriorityArrowDown color={color} />}
      {level === 'lowest' && <PriorityArrowDown color={color} />}
    </span>
  )
}

// ─── Status Badge ───────────────────────────────────────────────

const statusCategoryClass: Record<string, string> = {
  todo: 'badge-neutral',
  in_progress: 'badge-info',
  done: 'badge-success',
  cancelled: 'badge-warning',
}

function StatusBadge({ name, category }: { name: string; category: string }) {
  return (
    <span className={`badge ${statusCategoryClass[category] ?? 'badge-neutral'}`}>
      {name}
    </span>
  )
}

// ─── Helpers ────────────────────────────────────────────────────

function formatDateRange(start?: string, end?: string): string {
  const fmt = (d: string) => {
    const date = new Date(d + 'T00:00:00')
    if (isNaN(date.getTime())) return ''
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  const s = start ? fmt(start) : ''
  const e = end ? fmt(end) : ''
  if (!s && !e) return ''
  if (s && e) return `${s} - ${e}`
  return s || e
}

// ─── Main Component ─────────────────────────────────────────────

export default function SprintImportModal({ onClose, onImport, appMode, jiraStatus }: SprintImportModalProps) {
  // ── Instance state ──────────────────────────────────────────
  const activeInstances = useMemo<JiraInstanceInfo[]>(
    () => jiraStatus?.instances?.filter(i => i.active) ?? [],
    [jiraStatus]
  )
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>(
    activeInstances[0]?.cloudId ?? ''
  )
  const [instanceDropdownOpen, setInstanceDropdownOpen] = useState(false)
  const showInstancePicker = activeInstances.length > 1

  const selectedInstance = useMemo(
    () => activeInstances.find((i) => i.cloudId === selectedInstanceId) ?? null,
    [activeInstances, selectedInstanceId]
  )

  // ── Board state ─────────────────────────────────────────────
  const [boards, setBoards] = useState<JiraBoard[]>([])
  const [boardsLoading, setBoardsLoading] = useState(false)
  const [boardsReason, setBoardsReason] = useState<BoardsReason | undefined>()
  const [projectKey, setProjectKey] = useState('')
  const [selectedBoardId, setSelectedBoardId] = useState<number | null>(null)
  const [boardSearch, setBoardSearch] = useState('')

  // ── Large instance (project key gate) state ────────────────
  const [isLargeInstance, setIsLargeInstance] = useState(false)
  const [savedProjectKeys, setSavedProjectKeys] = useState<string[]>([])
  const [activeProjectKey, setActiveProjectKey] = useState('')
  const [projectKeyInput, setProjectKeyInput] = useState('')
  const [showRecentDropdown, setShowRecentDropdown] = useState(false)

  // Detect input that doesn't look like a project key (has space, too long, or invalid chars)
  const projectKeyLooksInvalid = useMemo(() => {
    const trimmed = projectKeyInput.trim()
    if (trimmed.length === 0) return false
    return /\s/.test(trimmed) || !/^[A-Z][A-Z0-9_]*$/.test(trimmed)
  }, [projectKeyInput])

  // Derive connectionId for the selected instance
  const connectionId = useMemo(() => {
    return jiraStatus?.connections?.find(c =>
      c.instances.some(i => i.cloudId === selectedInstanceId)
    )?.connectionId ?? null
  }, [jiraStatus, selectedInstanceId])

  async function loadBoards(searchProjectKey?: string) {
    setBoardsLoading(true)
    setBoardsReason(undefined)
    try {
      const result = await getJiraBoards(searchProjectKey, selectedInstanceId || undefined)
      setBoards(result.boards)
      setBoardsReason(result.reason)
      if (result.boards.length > 0) {
        setSelectedBoardId(result.boards[0].id)
      } else {
        setSelectedBoardId(null)
      }
    } catch {
      setBoardsReason('api_error')
      setBoards([])
    } finally {
      setBoardsLoading(false)
    }
  }

  // Load boards when instance changes
  useEffect(() => {
    let cancelled = false
    setBoardSearch('')
    setSprintFilter('active')
    setSelectedSprintId(null)
    setSelectedIssueKeys(new Set())
    setIssueSearch('')
    setAllSprints([])
    setAllIssues([])
    setBoardsLoading(true)
    setBoardsReason(undefined)
    // Reset large instance state
    setIsLargeInstance(false)
    setActiveProjectKey('')
    setProjectKeyInput('')
    setSavedProjectKeys([])
    getJiraBoards(undefined, selectedInstanceId || undefined)
      .then(result => {
        if (cancelled) return
        // Detect large instances (500+ boards)
        if (result.totalAvailable !== undefined && result.totalAvailable >= 500) {
          setIsLargeInstance(true)
          setBoards([])
          setSelectedBoardId(null)
          setBoardsReason(result.reason)
          // Load saved project keys from the connection
          const conn = jiraStatus?.connections?.find(c =>
            c.instances.some(i => i.cloudId === selectedInstanceId)
          )
          if (conn?.savedProjectKeys) {
            setSavedProjectKeys(conn.savedProjectKeys)
          }
        } else {
          setBoards(result.boards)
          setBoardsReason(result.reason)
          setSelectedBoardId(result.boards.length > 0 ? result.boards[0].id : null)
        }
      })
      .catch(() => {
        if (cancelled) return
        setBoardsReason('api_error')
        setBoards([])
      })
      .finally(() => { if (!cancelled) setBoardsLoading(false) })
    return () => { cancelled = true }
  }, [selectedInstanceId, jiraStatus])

  const filteredBoards = useMemo(() => {
    if (!boardSearch.trim()) return boards
    const q = boardSearch.toLowerCase()
    return boards.filter(
      (b) => b.name.toLowerCase().includes(q) || b.projectKey.toLowerCase().includes(q)
    )
  }, [boards, boardSearch])

  // ── Sprint state ────────────────────────────────────────────
  const [sprintFilter, setSprintFilter] = useState<SprintStateFilter>('active')
  const [selectedSprintId, setSelectedSprintId] = useState<number | null>(null)
  const [allSprints, setAllSprints] = useState<JiraSprint[]>([])
  const [sprintsLoading, setSprintsLoading] = useState(false)

  useEffect(() => {
    if (!selectedBoardId) {
      setAllSprints([])
      return
    }
    let cancelled = false
    setSprintsLoading(true)
    getJiraSprints(selectedBoardId, 'active,future,closed', selectedInstanceId || undefined)
      .then(result => { if (!cancelled) setAllSprints(result.sprints) })
      .catch(() => { if (!cancelled) setAllSprints([]) })
      .finally(() => { if (!cancelled) setSprintsLoading(false) })
    return () => { cancelled = true }
  }, [selectedBoardId, selectedInstanceId])

  const filteredSprints = useMemo(() => {
    return allSprints.filter((s) => s.state === sprintFilter)
  }, [allSprints, sprintFilter])

  // Auto-select first sprint when board or filter changes
  useEffect(() => {
    if (filteredSprints.length > 0) {
      setSelectedSprintId(filteredSprints[0].id)
    } else {
      setSelectedSprintId(null)
    }
    setSelectedIssueKeys(new Set())
    setIssueSearch('')
  }, [filteredSprints])

  const selectedSprint = useMemo(
    () => allSprints.find((s) => s.id === selectedSprintId) ?? null,
    [allSprints, selectedSprintId]
  )

  const selectedBoard = useMemo(
    () => boards.find((b) => b.id === selectedBoardId) ?? null,
    [boards, selectedBoardId]
  )

  // ── Issue state ─────────────────────────────────────────────
  const [issueSearch, setIssueSearch] = useState('')
  const [selectedIssueKeys, setSelectedIssueKeys] = useState<Set<string>>(new Set())
  const [hideCompleted, setHideCompleted] = useState(appMode === 'risk-prioritization')
  const [allIssues, setAllIssues] = useState<MappedIssue[]>([])
  const [issuesLoading, setIssuesLoading] = useState(false)

  useEffect(() => {
    if (!selectedSprintId) {
      setAllIssues([])
      return
    }
    let cancelled = false
    setIssuesLoading(true)
    getJiraSprintIssues(selectedSprintId, selectedInstanceId || undefined)
      .then(result => {
        if (cancelled) return
        const mapped = result.issues.map(mapIssue)
        setAllIssues(mapped)
        if (appMode === 'risk-prioritization') {
          const activeKeys = mapped.filter(i => !isCompletedIssue(i)).map(i => i.key)
          setSelectedIssueKeys(new Set(activeKeys))
        } else {
          setSelectedIssueKeys(new Set(mapped.map(i => i.key)))
        }
      })
      .catch(() => { if (!cancelled) setAllIssues([]) })
      .finally(() => { if (!cancelled) setIssuesLoading(false) })
    return () => { cancelled = true }
  }, [selectedSprintId, selectedInstanceId, appMode])

  const completedCount = useMemo(
    () => allIssues.filter(isCompletedIssue).length,
    [allIssues]
  )

  const visibleIssues = useMemo(() => {
    let list = allIssues
    if (hideCompleted) {
      list = list.filter((i) => !isCompletedIssue(i))
    }
    if (issueSearch.trim()) {
      const q = issueSearch.toLowerCase()
      list = list.filter(
        (i) => i.key.toLowerCase().includes(q) || i.summary.toLowerCase().includes(q)
      )
    }
    return list
  }, [allIssues, hideCompleted, issueSearch])

  // ── Callbacks ───────────────────────────────────────────────

  const handleInstanceSelect = useCallback((cloudId: string) => {
    setSelectedInstanceId(cloudId)
    setInstanceDropdownOpen(false)
  }, [])

  const handleBoardSelect = useCallback((boardId: number) => {
    setSelectedBoardId(boardId)
  }, [])

  const handleSprintSelect = useCallback((sprintId: number) => {
    setSelectedSprintId(sprintId)
    setSelectedIssueKeys(new Set())
    setIssueSearch('')
  }, [])

  const toggleIssue = useCallback((key: string) => {
    setSelectedIssueKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  const toggleSelectAll = useCallback(() => {
    const allVisible = visibleIssues.map((i) => i.key)
    const allSelected = allVisible.every((k) => selectedIssueKeys.has(k))
    if (allSelected) {
      setSelectedIssueKeys((prev) => {
        const next = new Set(prev)
        allVisible.forEach((k) => next.delete(k))
        return next
      })
    } else {
      setSelectedIssueKeys((prev) => {
        const next = new Set(prev)
        allVisible.forEach((k) => next.add(k))
        return next
      })
    }
  }, [visibleIssues, selectedIssueKeys])

  const allVisibleSelected =
    visibleIssues.length > 0 && visibleIssues.every((i) => selectedIssueKeys.has(i.key))
  const someVisibleSelected =
    visibleIssues.some((i) => selectedIssueKeys.has(i.key)) && !allVisibleSelected

  // ── Large instance handlers ────────────────────────────────

  function handleProjectKeySubmit() {
    const key = projectKeyInput.trim().toUpperCase()
    if (!key) return
    setActiveProjectKey(key)
    setProjectKeyInput('')
    setShowRecentDropdown(false)
    loadBoards(key)
    // Auto-save
    if (connectionId) {
      addSavedProjectKey(connectionId, key).then(r => setSavedProjectKeys(r.keys)).catch(() => {})
    }
  }

  function handleSelectSavedKey(key: string) {
    setActiveProjectKey(key)
    setShowRecentDropdown(false)
    setProjectKeyInput('')
    loadBoards(key)
  }

  function handleRemoveSavedKey(key: string) {
    setSavedProjectKeys(prev => prev.filter(k => k !== key))
    if (connectionId) {
      removeSavedProjectKey(connectionId, key).catch(() => {})
    }
  }

  function handleClearProjectKey() {
    setActiveProjectKey('')
    setBoards([])
    setSelectedBoardId(null)
  }

  // ── Render ──────────────────────────────────────────────────

  return (
    <div className="modal-overlay animate-fade-in" onClick={onClose}>
      <div
        className="modal w-full max-w-5xl h-[80vh] flex flex-col animate-scale-in"
        role="dialog"
        aria-label="Import from Sprint"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ─────────────────────────────────────────── */}
        <div className="modal-header">
          <h3 className="text-sm font-medium text-linear-text">Import from Sprint</h3>
          <button
            className="btn btn-ghost !p-1.5 text-linear-text-muted hover:text-linear-text"
            aria-label="Close"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>

        {/* ── Body: Three Columns ────────────────────────────── */}
        <div className="flex flex-1 min-h-0">
          {/* ── Left Column: Boards ───────────────────────────── */}
          <div className="w-[200px] shrink-0 border-r border-linear-border flex flex-col">
            {showInstancePicker && (
              <div className="relative px-2 pt-2 pb-1 border-b border-linear-border-subtle">
                <button
                  onClick={() => setInstanceDropdownOpen(!instanceDropdownOpen)}
                  className="btn btn-ghost !px-2.5 !py-1.5 !gap-2 w-full !justify-start text-linear-text-secondary hover:text-linear-text"
                >
                  <InstanceAvatar name={selectedInstance?.cloudName ?? ''} />
                  <span className="text-sm font-medium truncate flex-1 text-left">{selectedInstance?.cloudName}</span>
                  <ChevronDown className="w-3 h-3 text-linear-text-muted shrink-0" />
                </button>
                {instanceDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setInstanceDropdownOpen(false)}
                    />
                    <div className="dropdown absolute top-full left-0 mt-1 z-20 min-w-[200px] py-1">
                      {activeInstances.map((inst) => (
                        <button
                          key={inst.cloudId}
                          onClick={() => handleInstanceSelect(inst.cloudId)}
                          className={`dropdown-item w-full text-left ${
                            inst.cloudId === selectedInstanceId ? 'active' : ''
                          }`}
                        >
                          <InstanceAvatar name={inst.cloudName} />
                          <div className="flex flex-col">
                            <span className="text-sm">{inst.cloudName}</span>
                            <span className="text-[11px] text-linear-text-muted">{inst.cloudUrl}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            {isLargeInstance && !activeProjectKey ? (
              /* ── Large instance: project key gate ─────────────── */
              <div className="px-3 pt-3 flex-1 flex flex-col">
                <label htmlFor="project-key-input" className="text-[10px] font-medium uppercase tracking-wide text-linear-text-muted mb-2">Project Key</label>

                {/* Input + Go button */}
                <div className="relative">
                  <div className="flex gap-1.5">
                    <input
                      id="project-key-input"
                      className={`input input-sm !text-xs !py-1.5 flex-1 ${projectKeyLooksInvalid ? '!border-warning/60' : ''}`}
                      placeholder="e.g. MAPS, RED, BOLO"
                      value={projectKeyInput}
                      onChange={e => setProjectKeyInput(e.target.value.toUpperCase().replace(/\s+/g, ''))}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.nativeEvent.isComposing && !projectKeyLooksInvalid) {
                          handleProjectKeySubmit()
                        }
                      }}
                      onFocus={() => setShowRecentDropdown(true)}
                      maxLength={20}
                      aria-invalid={projectKeyLooksInvalid}
                      aria-describedby={projectKeyLooksInvalid ? 'project-key-hint' : undefined}
                    />
                    <button
                      className="btn btn-primary btn-sm !px-2.5"
                      disabled={!projectKeyInput.trim() || projectKeyLooksInvalid}
                      onClick={handleProjectKeySubmit}
                    >
                      Go
                    </button>
                  </div>

                  {/* Validation hint */}
                  {projectKeyLooksInvalid && (
                    <div id="project-key-hint" role="alert" className="mt-2 p-2 rounded bg-warning/10 border border-warning/40">
                      <p className="text-[11px] text-warning leading-relaxed">
                        That looks like a board name. A project key is a short code like <span className="font-mono font-semibold">MAPS</span>, usually 2 to 10 letters.
                      </p>
                    </div>
                  )}

                  {/* Recent dropdown — hidden while input is invalid to avoid overlap with the hint */}
                  {showRecentDropdown && savedProjectKeys.length > 0 && !projectKeyLooksInvalid && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowRecentDropdown(false)} />
                      <div className="absolute left-0 right-0 top-full mt-1 bg-linear-bg-card border border-linear-border rounded-lg shadow-lg z-20 py-1">
                        <p className="px-3 py-1 text-[10px] text-linear-text-muted uppercase tracking-wide">Recent</p>
                        {savedProjectKeys.map(key => (
                          <div key={key} className="flex items-center justify-between px-3 py-1.5 hover:bg-linear-bg-hover cursor-pointer" onClick={() => handleSelectSavedKey(key)}>
                            <span className="text-[12px] text-linear-text-secondary">{key}</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleRemoveSavedKey(key) }}
                              className="text-linear-text-placeholder hover:text-error transition-colors"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Empty state */}
                <div className="flex-1 flex items-center justify-center px-2">
                  <p className="text-[11px] text-linear-text-placeholder text-center leading-relaxed">
                    This instance has too many boards to load at once. Enter a project key to get started.
                  </p>
                </div>
              </div>
            ) : isLargeInstance && activeProjectKey ? (
              /* ── Large instance: active project with boards ───── */
              <>
                <div className="px-3 pt-3 pb-2">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-linear-text-muted">Project</span>
                </div>
                <div className="px-3 pb-2">
                  <div className="flex items-center justify-between px-3 py-2 rounded-md bg-linear-accent/10 border border-linear-accent/20">
                    <span className="text-[13px] font-medium text-linear-accent-focus">{activeProjectKey}</span>
                    <button onClick={handleClearProjectKey} className="text-linear-text-muted hover:text-linear-text transition-colors">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="px-3 pb-1">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-linear-text-muted">Boards</span>
                </div>
                <div className="px-3 pb-2">
                  <SearchInput
                    value={boardSearch}
                    onChange={setBoardSearch}
                    placeholder="Search boards..."
                  />
                </div>
                <div className="flex-1 overflow-y-auto px-1.5 pb-2">
                  {boardsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="spinner spinner-sm"></div>
                    </div>
                  ) : boards.length === 0 ? (
                    <div className="px-2 py-4 text-center text-xs text-linear-text-muted">
                      No boards found for {activeProjectKey}
                    </div>
                  ) : filteredBoards.length === 0 ? (
                    <div className="px-2 py-4 text-center text-xs text-linear-text-muted">
                      No boards found
                    </div>
                  ) : (
                    filteredBoards.map((board) => (
                      <BoardItem
                        key={board.id}
                        board={board}
                        isSelected={board.id === selectedBoardId}
                        onClick={() => handleBoardSelect(board.id)}
                      />
                    ))
                  )}
                </div>
              </>
            ) : (
              /* ── Normal instance: standard boards UI ──────────── */
              <>
                <div className="px-3 pt-3 pb-2">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-linear-text-muted">
                    Boards
                  </span>
                </div>
                <div className="px-3 pb-2">
                  <SearchInput
                    value={boardSearch}
                    onChange={setBoardSearch}
                    placeholder="Search boards..."
                  />
                </div>
                <div className="flex-1 overflow-y-auto px-1.5 pb-2">
                  {boardsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="spinner spinner-sm"></div>
                    </div>
                  ) : boards.length === 0 && boardsReason ? (
                    <BoardsErrorMessage reason={boardsReason} />
                  ) : !boardsLoading && boards.length === 0 && !boardsReason ? (
                    <div className="px-3 py-4 text-center">
                      <p className="text-xs text-linear-text-muted mb-3">No boards found. Try a project key:</p>
                      <div className="flex gap-2">
                        <input
                          className="input input-sm flex-1"
                          placeholder="e.g. BAT"
                          value={projectKey}
                          onChange={e => setProjectKey(e.target.value.toUpperCase())}
                          onKeyDown={e => { if (e.key === 'Enter' && projectKey.trim()) loadBoards(projectKey.trim()) }}
                          maxLength={20}
                        />
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={!projectKey.trim()}
                          onClick={() => loadBoards(projectKey.trim())}
                        >
                          Search
                        </button>
                      </div>
                    </div>
                  ) : filteredBoards.length === 0 ? (
                    <div className="px-2 py-4 text-center text-xs text-linear-text-muted">
                      No boards found
                    </div>
                  ) : (
                    filteredBoards.map((board) => (
                      <BoardItem
                        key={board.id}
                        board={board}
                        isSelected={board.id === selectedBoardId}
                        onClick={() => handleBoardSelect(board.id)}
                      />
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          {/* ── Middle Column: Sprints ────────────────────────── */}
          <div className="w-[240px] shrink-0 border-r border-linear-border flex flex-col">
            <div className="px-3 pt-3 pb-2">
              <span className="text-xs font-medium text-linear-text-secondary truncate block">
                {selectedBoard?.name ?? 'Select a board'}
              </span>
            </div>
            <div className="px-3 pb-2">
              <div className="segmented-control w-full">
                {(['active', 'future', 'closed'] as const).map((state) => (
                  <button
                    key={state}
                    onClick={() => setSprintFilter(state)}
                    className={`segmented-control-option flex-1 text-center !text-xs !px-2 !py-1 ${
                      sprintFilter === state ? 'active' : ''
                    }`}
                    style={
                      sprintFilter === state
                        ? { backgroundColor: 'var(--linear-bg-hover)', color: 'var(--linear-text)' }
                        : undefined
                    }
                  >
                    {state.charAt(0).toUpperCase() + state.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-1.5 pb-2">
              {sprintsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="spinner spinner-sm"></div>
                </div>
              ) : filteredSprints.length === 0 ? (
                <div className="px-2 py-4 text-center text-xs text-linear-text-muted">
                  No {sprintFilter} sprints
                </div>
              ) : (
                filteredSprints.map((sprint) => (
                  <SprintItem
                    key={sprint.id}
                    sprint={sprint}
                    isSelected={sprint.id === selectedSprintId}
                    onClick={() => handleSprintSelect(sprint.id)}
                  />
                ))
              )}
            </div>
          </div>

          {/* ── Right Column: Issues ──────────────────────────── */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="px-4 pt-3 pb-2 flex items-center gap-3">
              <span className="text-xs font-medium text-linear-text-secondary truncate">
                {selectedSprint?.name ?? 'Select a sprint'}
              </span>
              <div className="flex-1" />
              {visibleIssues.length > 0 && (
                <span className="text-[11px] text-linear-text-muted shrink-0">
                  {selectedIssueKeys.size} selected
                </span>
              )}
            </div>
            <div className="px-4 pb-2 flex items-center gap-2">
              <SearchInput
                value={issueSearch}
                onChange={setIssueSearch}
                placeholder="Search issues..."
                className="flex-1"
              />
              {visibleIssues.length > 0 && (
                <button
                  onClick={toggleSelectAll}
                  className="btn btn-ghost !text-xs !px-2 !py-1 shrink-0 text-linear-text-muted hover:text-linear-text"
                >
                  <Checkbox
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                  />
                  <span>All ({visibleIssues.length})</span>
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto px-2 pb-2">
              {issuesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="spinner spinner-sm"></div>
                </div>
              ) : selectedSprintId === null ? (
                <div className="flex items-center justify-center h-full text-sm text-linear-text-muted">
                  Select a sprint to view issues
                </div>
              ) : visibleIssues.length === 0 ? (
                <div className="flex items-center justify-center h-full text-sm text-linear-text-muted">
                  {issueSearch ? 'No matching issues' : 'No issues in this sprint'}
                </div>
              ) : (
                visibleIssues.map((issue) => (
                  <IssueRow
                    key={issue.key}
                    issue={issue}
                    isSelected={selectedIssueKeys.has(issue.key)}
                    isCompleted={isCompletedIssue(issue)}
                    onToggle={() => toggleIssue(issue.key)}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────── */}
        <div className="modal-footer !justify-between">
          <div className="flex items-center gap-2">
            {appMode === 'risk-prioritization' && (
              <label className="flex items-center gap-2 cursor-pointer select-none group">
                <Checkbox
                  checked={hideCompleted}
                  onChange={() => setHideCompleted(!hideCompleted)}
                />
                <span className="text-xs text-linear-text-muted group-hover:text-linear-text-secondary transition-colors">
                  Hide completed/cancelled ({completedCount})
                </span>
              </label>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button
              className="btn btn-primary"
              disabled={selectedIssueKeys.size === 0}
              onClick={() => onImport([...selectedIssueKeys])}
            >
              Import {selectedIssueKeys.size > 0 ? `${selectedIssueKeys.size} ` : ''}
              Ticket{selectedIssueKeys.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Boards Error Message ───────────────────────────────────────

function BoardsErrorMessage({ reason }: { reason: BoardsReason }) {
  return (
    <div className="px-3 py-4 text-center">
      <svg className="w-8 h-8 text-linear-text-muted mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      {(reason === 'no_token' || reason === 'no_cloud_info') && (
        <>
          <p className="text-xs text-linear-text mb-1">JIRA is not connected.</p>
          <p className="text-[11px] text-linear-text-muted mb-2">Connect your JIRA account to import tickets from sprints.</p>
          <Link to="/settings" className="text-xs text-linear-accent hover:underline">Go to Settings</Link>
        </>
      )}
      {reason === 'token_expired' && (
        <>
          <p className="text-xs text-linear-text mb-1">Your JIRA connection has expired.</p>
          <p className="text-[11px] text-linear-text-muted mb-2">Reconnect your JIRA account to continue importing sprints.</p>
          <Link to="/settings" className="text-xs text-linear-accent hover:underline">Reconnect in Settings</Link>
        </>
      )}
      {reason === 'invalid_cloud_id' && (
        <>
          <p className="text-xs text-linear-text mb-1">Your JIRA connection looks corrupted.</p>
          <p className="text-[11px] text-linear-text-muted mb-2">Try disconnecting and reconnecting your JIRA account.</p>
          <Link to="/settings" className="text-xs text-linear-accent hover:underline">Go to Settings</Link>
        </>
      )}
      {reason === 'api_forbidden' && (
        <>
          <p className="text-xs text-linear-text mb-1">JIRA denied access to the boards API.</p>
          <p className="text-[11px] text-linear-text-muted mb-2">Your JIRA permissions may have changed, or the connection needs refreshing.</p>
          <Link to="/settings" className="text-xs text-linear-accent hover:underline">Reconnect in Settings</Link>
        </>
      )}
      {reason === 'api_error' && (
        <>
          <p className="text-xs text-linear-text mb-1">Something went wrong loading boards.</p>
          <p className="text-[11px] text-linear-text-muted">Try again in a moment. If the problem persists, try reconnecting in <Link to="/settings" className="text-linear-accent hover:underline">Settings</Link>.</p>
        </>
      )}
    </div>
  )
}

// ─── Sub-components ─────────────────────────────────────────────

function Checkbox({
  checked,
  indeterminate = false,
  onChange,
}: {
  checked: boolean
  indeterminate?: boolean
  onChange?: () => void
}) {
  return (
    <span
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      onClick={onChange}
      className={`
        inline-flex items-center justify-center w-4 h-4 rounded border shrink-0 transition-all cursor-pointer
        ${
          checked || indeterminate
            ? 'bg-linear-accent border-linear-accent'
            : 'border-linear-border-input bg-transparent hover:border-linear-text-muted'
        }
      `}
    >
      {checked && <CheckIcon checked />}
      {indeterminate && !checked && (
        <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 10" fill="none" aria-hidden="true">
          <path d="M2 5h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      )}
    </span>
  )
}

function BoardItem({
  board,
  isSelected,
  onClick,
}: {
  board: JiraBoard
  isSelected: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`
        w-full text-left px-2.5 py-2 rounded-md transition-colors group
        ${
          isSelected
            ? 'bg-linear-bg-hover border-l-2 border-linear-accent'
            : 'border-l-2 border-transparent hover:bg-linear-bg-hover/50'
        }
      `}
    >
      <div
        className={`text-[13px] font-medium truncate ${
          isSelected ? 'text-linear-text' : 'text-linear-text-secondary group-hover:text-linear-text'
        }`}
      >
        {board.name}
      </div>
      <div className="text-[11px] text-linear-text-muted truncate mt-0.5">
        {board.projectKey ? `${board.projectKey} · ${board.type}` : board.type}
      </div>
    </button>
  )
}

function SprintItem({
  sprint,
  isSelected,
  onClick,
}: {
  sprint: JiraSprint
  isSelected: boolean
  onClick: () => void
}) {
  const isActive = sprint.state === 'active'

  return (
    <button
      onClick={onClick}
      className={`
        w-full text-left px-2.5 py-2 rounded-md transition-colors group
        ${
          isSelected
            ? `bg-linear-bg-hover ${isActive ? 'border-l-2 border-success' : 'border-l-2 border-linear-accent'}`
            : 'border-l-2 border-transparent hover:bg-linear-bg-hover/50'
        }
      `}
    >
      <div className="flex items-center gap-2">
        <span
          className={`text-[13px] font-medium truncate flex-1 ${
            isSelected ? 'text-linear-text' : 'text-linear-text-secondary group-hover:text-linear-text'
          }`}
        >
          {sprint.name}
        </span>
        <SprintBadge state={sprint.state} />
      </div>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-[11px] text-linear-text-muted truncate">
          {formatDateRange(sprint.startDate, sprint.endDate)}
        </span>
      </div>
    </button>
  )
}

function IssueRow({
  issue,
  isSelected,
  isCompleted,
  onToggle,
}: {
  issue: MappedIssue
  isSelected: boolean
  isCompleted: boolean
  onToggle: () => void
}) {
  return (
    <button
      onClick={onToggle}
      className={`
        w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md transition-colors text-left group
        hover:bg-linear-bg-hover/50
        ${isSelected ? 'bg-linear-bg-hover' : ''}
        ${isCompleted ? 'opacity-50' : ''}
      `}
    >
      <Checkbox checked={isSelected} />
      <IssueTypeIcon type={issue.type.icon} />
      <span className="text-xs font-mono text-linear-accent shrink-0">{issue.key}</span>
      <span className="text-[13px] text-linear-text-secondary truncate flex-1 group-hover:text-linear-text">
        {issue.summary}
      </span>
      <StatusBadge name={issue.status.name} category={issue.status.category} />
      <PriorityIcon level={issue.priority.level} />
    </button>
  )
}
